/**
 * signedTax — the frontend half of the one change-order tax rule.
 *
 * RULED (Adrian, 2026-10-06, on IDI-455/IDI-458):
 *   "The CO's net change is taxed at the rate locked on the job's signed
 *    quote."
 *
 * The rate is NOT computed here and NOT read from tenant settings. It comes
 * from the job read's `signedTax` block (cmd/jobs/signed_tax.go), which
 * derives it from the immutable AcceptedConfiguration snapshot (or, for a job
 * with no snapshot, the IDI-491 fallback: migrated quote's rate, else the
 * dealer default) using internal/signedtax — the same package cmd/invoices bills an applied change
 * order with. That is deliberate: a change order that quotes one tax and
 * bills another is the failure IDI-458 exists to prevent, so there is exactly
 * one source and this module only applies it.
 *
 * Rounding mirrors the Go side (half away from zero) so a credit rounds by
 * the same magnitude as the equivalent charge, and the number shown here
 * matches the number billed to the cent.
 */

/**
 * Tax owed on a change order's net amount, in cents.
 *
 * @param {number} netCents           The CO's net change (negative = credit).
 * @param {object|null|undefined} signedTax  The job's `signedTax` block.
 * @returns {number} cents of tax (negative for a credit)
 */
export function changeOrderTaxCents(netCents, signedTax) {
  const net = Number(netCents);
  if (!Number.isFinite(net) || net === 0) return 0;

  const taxable = Number(signedTax?.taxableSubtotalCents);
  const tax = Number(signedTax?.taxCents);
  // An exempt or untaxed signed quote stays untaxed for every change order on
  // it, whatever the tenant's settings say today. An unknown snapshot
  // (`source: 'none' | 'unavailable'`) also yields 0 — see taxIsKnown, which
  // is what the UI uses to avoid presenting that 0 as a fact.
  if (!Number.isFinite(taxable) || !Number.isFinite(tax) || taxable <= 0 || tax <= 0) return 0;

  // Half away from zero, matching Go's divRoundHalfAwayFromZero: negate
  // before rounding so a credit rounds by the same magnitude as the
  // equivalent charge (plain Math.round(-0.5) is -0, i.e. toward zero).
  const exact = (net * tax) / taxable;
  const rounded = exact < 0 ? -Math.round(-exact) : Math.round(exact);
  // `+ 0` normalises -0 to 0. A credit that rounds to nothing must not
  // render as "-$0.00" (caught by the cross-language fixture test).
  return rounded + 0;
}

/**
 * Whether the job's signed tax position is actually known.
 *
 * `false` means the job has no accepted-configuration snapshot (converted
 * before acceptance, or migrated) or it couldn't be loaded. The caller must
 * not present $0.00 as a fact in that case — showing a confident zero is how
 * a taxable change order gets under-billed.
 *
 * @param {object|null|undefined} signedTax
 * @returns {boolean}
 */
export function taxIsKnown(signedTax) {
  // IDI-491 (U-27): a job with no signed snapshot is taxed at the migrated
  // quote's legacy rate, else the dealer's default. Both are known bases.
  return KNOWN_SOURCES.has(signedTax?.source);
}

const KNOWN_SOURCES = new Set(['accepted_configuration', 'migrated_quote', 'dealer_default']);

/**
 * IDI-491: the caption shown wherever tax shows. The backend writes it from
 * the shared rule (internal/signedtax Basis.Caption); this falls back to the
 * same wording for an older response that has no caption.
 * @param {object|null|undefined} signedTax
 * @returns {string}
 */
export function taxCaption(signedTax) {
  if (signedTax?.caption) return signedTax.caption;
  const pct = `${(signedTaxRate(signedTax) * 100).toFixed(4).replace(/\.?0+$/, '')}%`;
  switch (signedTax?.source) {
    case 'accepted_configuration':
      return signedTax.exempt ? 'Tax exempt (from the signed quote)' : `Taxed at ${pct} (from the signed quote)`;
    case 'migrated_quote': return `Taxed at ${pct} (from the migrated quote)`;
    case 'dealer_default': return `Taxed at ${pct} (your default tax rate)`;
    case 'unavailable': return 'Tax unavailable';
    default: return 'No tax rate';
  }
}

/**
 * The signed rate as a decimal, for display ("taxed at 6.875%").
 *
 * @param {object|null|undefined} signedTax
 * @returns {number}
 */
export function signedTaxRate(signedTax) {
  const r = Number(signedTax?.rate);
  return Number.isFinite(r) && r > 0 ? r : 0;
}

export default changeOrderTaxCents;
